import { SectionTitle } from '@/components/blocks/SectionTitle';
import { DepartmentList } from '@/components/content/SiteMap/parts/DepartmentList';
import { SiteMapNavigate } from '@/components/content/SiteMap/parts/SiteMapNavigate';
import { StaticBreadcrumbTrail } from '@/components/content/StaticBreadcrumbTrail';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { Box, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { siteMapSX } from './styles/siteMapSX';

export const SiteMap: FC<{ id: ID }> = ({}) => {
	const localization = useLocalization('SiteMap');
	const title = localization.title.t();
	const JSONData = `{'${title}':''}`;
	const properties = { JSONData: JSONData };

	const STICKY_NAV_HEIGHT = 80;
	const [isSticky, setIsSticky] = useState(false);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const navRef = useRef<HTMLDivElement>(null);
	const departmentsRef = useRef<HTMLDivElement>(null);
	const sectionsRef = useRef<HTMLDivElement>(null);

	const { navigation } = useGetCategories();
	const hasDepartments = useMemo(() => {
		if (!navigation || navigation.length === 0) return false;
		return (navigation[0]?.children?.length ?? 0) > 0;
	}, [navigation]);

	const hasOtherSections = false;

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsSticky(!entry.isIntersecting);
			},
			{ threshold: 0 }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, []);

	const scrollWithOffset = (ref: React.RefObject<HTMLDivElement>) => {
		if (!ref.current) return;
		const navHeight = navRef.current?.offsetHeight ?? STICKY_NAV_HEIGHT;
		const top = ref.current.getBoundingClientRect().top + window.scrollY - navHeight;
		window.scrollTo({ top, behavior: 'smooth' });
	};

	const handleGoDepartments = () => scrollWithOffset(departmentsRef);
	const handleGoSections = () => scrollWithOffset(sectionsRef);

	return (
		<>
			<StaticBreadcrumbTrail id="site-map-breadcrumb" properties={properties} />
			<SectionTitle title={title} />

			<Box ref={sentinelRef} sx={{ height: 0 }} />

			<Box ref={navRef} sx={isSticky ? siteMapSX.stickyNavWrapper : siteMapSX.NavWrapper}>
				<SiteMapNavigate
					isSticky={isSticky}
					hasDepartments={hasDepartments}
					hasOtherSections={hasOtherSections}
					handleGoDepartments={handleGoDepartments}
					handleGoSections={handleGoSections}
				/>
			</Box>

			<Box ref={departmentsRef} sx={siteMapSX.departmentsContainer}>
				<DepartmentList />
			</Box>

			{hasOtherSections && (
				<Box ref={sectionsRef} sx={siteMapSX.otherSectionsContainer}>
					<Typography sx={siteMapSX.otherSectionsTitle}>
						{localization.otherSections.t()}
					</Typography>
				</Box>
			)}
		</>
	);
};
